import { useEffect, useState, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import DashboardLayout from "../../components/DashboardLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { Textarea } from "../../components/ui/textarea";
import { ScrollArea } from "../../components/ui/scroll-area";
import {
  Users,
  UserX,
  Play,
  Search,
  Upload,
  Download,
  Plus,
  X,
  CheckCircle,
  AlertCircle,
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight,
  Mail,
  MapPin,
  Phone,
  FileText,
  Calendar,
  Building2,
  Send,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";

const INVESTMENT_FOCUS_OPTIONS = [
  "Single Family",
  "Multi Family",
  "Commercial",
  "Industrial",
  "Flips",
  "Development",
  "Condos/Townhomes",
  "Land",
  "Luxury Properties",
  "Home Additions",
  "Rentals",
];

const US_STATES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

const ITEMS_PER_PAGE = 10;

const ROLE_CONFIG = {
  investor: { label: "Investors", singular: "Investor" },
  agent: { label: "Agents", singular: "Agent" },
  wholesaler: { label: "Wholesalers", singular: "Wholesaler" },
  seller: { label: "Sellers", singular: "Seller" },
};

const InvestorManagementPage = () => {
  const [searchParams] = useSearchParams();
  const urlRole = searchParams.get("role") || "investor";
  const roleConfig = ROLE_CONFIG[urlRole] || ROLE_CONFIG.investor;

  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editInvestor, setEditInvestor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importData, setImportData] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [importing, setImporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const fileInputRef = useRef(null);

  // Single investor form
  const [newInvestor, setNewInvestor] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    available_capital: "",
    primary_investment_focus: [],
    areas_of_focus_state: "",
    areas_of_focus_regions: [],
    years_experience: "",
    is_wholesaler: "",
    hard_money_funding: "",
    comfortable_wholesale_fee: "",
    agrees_finder_fee: "",
    will_provide_relisting: "",
    notes: "",
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchInvestors = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (urlRole === "investor") {
        const response = await axios.get(`${API}/admin/investors`, {
          withCredentials: true,
          headers,
        });
        setInvestors(response.data);
      } else {
        const response = await axios.get(`${API}/admin/users`, {
          withCredentials: true,
          headers,
        });
        setInvestors(response.data.filter((u) => u.role === urlRole));
      }
    } catch (error) {
      console.error(`Failed to fetch ${roleConfig.label}:`, error);
      toast.error(`Failed to fetch ${roleConfig.label}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSearchQuery("");
    setStateFilter("all");
    setCurrentPage(1);
    fetchInvestors();
  }, [urlRole]);

  // Get unique states from investors for filter
  const availableStates = useMemo(() => {
    const states = [
      ...new Set(investors.map((inv) => inv.state).filter(Boolean)),
    ];
    return states.sort();
  }, [investors]);

  // Filter and paginate investors
  const filteredInvestors = useMemo(() => {
    let result = [...investors];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (investor) =>
          investor.name?.toLowerCase().includes(query) ||
          investor.email?.toLowerCase().includes(query) ||
          investor.state?.toLowerCase().includes(query),
      );
    }

    // State filter
    if (stateFilter !== "all") {
      result = result.filter((investor) => investor.state === stateFilter);
    }

    return result;
  }, [investors, searchQuery, stateFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredInvestors.length / ITEMS_PER_PAGE);
  const paginatedInvestors = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredInvestors.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredInvestors, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, stateFilter]);

  const handleInvestorAction = async (userId, action) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(
        `${API}/admin/investors/${userId}/action`,
        { action },
        { withCredentials: true, headers },
      );
      toast.success(`Action "${action}" completed successfully`);
      fetchInvestors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    }
  };

  const handleSendInvite = async (investorIds) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(
        `${API}/admin/investors/send-invite`,
        { investor_ids: investorIds },
        { withCredentials: true, headers },
      );

      if (response.data.email_configured) {
        toast.success(
          `${response.data.sent_count} invite email(s) sent successfully!`,
        );
      } else {
        toast.info(
          response.data.note ||
            "In-app notifications sent. Configure email for full functionality.",
        );
      }

      fetchInvestors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    }
  };

  const handleDownloadSample = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/admin/investors/sample-csv`, {
        withCredentials: true,
        headers,
      });

      // Create CSV content
      const csvHeaders = response.data.headers.join(",");
      const csvRows = response.data.sample_rows.map((row) =>
        response.data.headers.map((h) => `"${row[h] || ""}"`).join(","),
      );
      const csvContent = [csvHeaders, ...csvRows].join("\n");

      // Download file
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "investor_import_template.csv";
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success("Sample template downloaded");
    } catch (error) {
      toast.error("Failed to download sample template");
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split("\n").filter((line) => line.trim());

      if (lines.length < 2) {
        toast.error("CSV file must have headers and at least one data row");
        return;
      }

      const headers = lines[0]
        .split(",")
        .map((h) => h.trim().toLowerCase().replace(/"/g, ""));
      const requiredHeaders = ["name", "email", "state"];

      const missingHeaders = requiredHeaders.filter(
        (h) => !headers.includes(h),
      );
      if (missingHeaders.length > 0) {
        toast.error(`Missing required columns: ${missingHeaders.join(", ")}`);
        return;
      }

      const data = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || [];
        const cleanValues = values.map((v) => v.replace(/^"|"$/g, "").trim());

        const row = {};
        headers.forEach((header, index) => {
          row[header] = cleanValues[index] || "";
        });

        if (row.name && row.email && row.state) {
          data.push(row);
        }
      }

      if (data.length === 0) {
        toast.error("No valid data rows found in CSV");
        return;
      }

      setImportData(data);
      toast.success(`${data.length} investors ready to import`);
    };

    reader.readAsText(file);
    e.target.value = "";
  };

  const handleImport = async () => {
    if (importData.length === 0) {
      toast.error("No data to import");
      return;
    }

    setImporting(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.post(
        `${API}/admin/investors/import`,
        importData,
        { withCredentials: true, headers },
      );

      setImportResults(response.data);

      if (response.data.success.length > 0) {
        toast.success(
          `Successfully imported ${response.data.success.length} investors`,
        );
        fetchInvestors();
      }

      if (response.data.failed.length > 0 || response.data.skipped.length > 0) {
        toast.warning(
          `${response.data.failed.length} failed, ${response.data.skipped.length} skipped`,
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleAddSingleInvestor = async () => {
    if (!newInvestor.name || !newInvestor.phone || !newInvestor.email) {
      toast.error("Please fill in name, phone number, and email");
      return;
    }

    setImporting(true);
    try {
      const headers = getAuthHeaders();
      const investorData = {
        name: newInvestor.name,
        email: newInvestor.email,
        phone: newInvestor.phone,
        address: newInvestor.address || undefined,
        state: newInvestor.areas_of_focus_state || undefined,
        available_capital: newInvestor.available_capital || undefined,
        primary_investment_focus:
          newInvestor.primary_investment_focus.length > 0
            ? newInvestor.primary_investment_focus
            : undefined,
        areas_of_focus_state: newInvestor.areas_of_focus_state || undefined,
        areas_of_focus_regions: newInvestor.areas_of_focus_regions,
        years_experience: newInvestor.years_experience
          ? parseInt(newInvestor.years_experience)
          : undefined,
        is_wholesaler: newInvestor.is_wholesaler || undefined,
        hard_money_funding: newInvestor.hard_money_funding || undefined,
        comfortable_wholesale_fee:
          newInvestor.comfortable_wholesale_fee || undefined,
        agrees_finder_fee: newInvestor.agrees_finder_fee || undefined,
        will_provide_relisting: newInvestor.will_provide_relisting || undefined,
        notes: newInvestor.notes || undefined,
      };
      const response = await axios.post(
        `${API}/admin/investors/import`,
        [investorData],
        { withCredentials: true, headers },
      );

      if (response.data.success.length > 0) {
        toast.success(`Investor "${newInvestor.name}" added successfully`);
        setShowAddModal(false);
        setNewInvestor({
          name: "",
          phone: "",
          email: "",
          address: "",
          available_capital: "",
          primary_investment_focus: [],
          areas_of_focus_state: "",
          areas_of_focus_regions: [],
          years_experience: "",
          is_wholesaler: "",
          hard_money_funding: "",
          comfortable_wholesale_fee: "",
          agrees_finder_fee: "",
          will_provide_relisting: "",
          notes: "",
        });
        fetchInvestors();

        const tempPass = response.data.success[0].temp_password;
        if (tempPass)
          toast.info(`Temporary password: ${tempPass}`, { duration: 10000 });
      } else if (response.data.skipped.length > 0) {
        toast.error(response.data.skipped[0].reason);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add investor");
    } finally {
      setImporting(false);
    }
  };

  const resetImportModal = () => {
    setShowImportModal(false);
    setImportData([]);
    setImportResults(null);
  };

  const handleInvestorClick = (investor) => {
    setSelectedInvestor(investor);
    setEditMode(false);
    setShowDetailModal(true);
  };

  const startEditInvestor = () => {
    const inv = selectedInvestor;
    setEditInvestor({
      name: inv.name || "",
      phone: inv.phone || "",
      email: inv.email || "",
      address: inv.address || "",
      available_capital: inv.available_capital || "",
      primary_investment_focus: Array.isArray(inv.primary_investment_focus)
        ? [...inv.primary_investment_focus]
        : inv.primary_investment_focus
          ? [inv.primary_investment_focus]
          : [],
      areas_of_focus_state: inv.areas_of_focus_state || "",
      areas_of_focus_regions: inv.areas_of_focus_regions || [],
      years_experience:
        inv.years_experience != null ? String(inv.years_experience) : "",
      is_wholesaler: inv.is_wholesaler || "",
      hard_money_funding: inv.hard_money_funding || "",
      comfortable_wholesale_fee: inv.comfortable_wholesale_fee || "",
      agrees_finder_fee: inv.agrees_finder_fee || "",
      will_provide_relisting: inv.will_provide_relisting || "",
      notes: inv.notes || "",
    });
    setEditMode(true);
  };

  const handleSaveInvestor = async () => {
    if (!editInvestor.name || !editInvestor.email) {
      toast.error("Name and email are required");
      return;
    }
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      const payload = {
        name: editInvestor.name,
        phone: editInvestor.phone || undefined,
        email: editInvestor.email || undefined,
        address: editInvestor.address || undefined,
        available_capital: editInvestor.available_capital || undefined,
        primary_investment_focus:
          editInvestor.primary_investment_focus.length > 0
            ? editInvestor.primary_investment_focus
            : undefined,
        areas_of_focus_state: editInvestor.areas_of_focus_state || undefined,
        areas_of_focus_regions: editInvestor.areas_of_focus_regions,
        years_experience: editInvestor.years_experience
          ? parseInt(editInvestor.years_experience)
          : undefined,
        is_wholesaler: editInvestor.is_wholesaler || undefined,
        hard_money_funding: editInvestor.hard_money_funding || undefined,
        comfortable_wholesale_fee:
          editInvestor.comfortable_wholesale_fee || undefined,
        agrees_finder_fee: editInvestor.agrees_finder_fee || undefined,
        will_provide_relisting:
          editInvestor.will_provide_relisting || undefined,
        notes: editInvestor.notes || undefined,
      };
      const response = await axios.put(
        `${API}/admin/investors/${selectedInvestor.user_id}`,
        payload,
        { withCredentials: true, headers },
      );
      toast.success("Investor updated successfully");
      setSelectedInvestor(response.data);
      setEditMode(false);
      fetchInvestors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update investor");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <DashboardLayout title={`${roleConfig.label} Management`}>
      <div
        className="space-y-6 animate-fade-in"
        data-testid="investor-management-page"
      >
        {/* Action Buttons — investor-specific */}
        {urlRole === "investor" && (
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => setShowAddModal(true)}
              data-testid="add-investor-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Investor
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowImportModal(true)}
              data-testid="import-investors-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import Investors
            </Button>
            <Button
              variant="outline"
              onClick={handleDownloadSample}
              data-testid="download-sample-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Sample CSV
            </Button>
          </div>
        )}

        {/* Users Table */}
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <CardTitle className="font-heading flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  All {roleConfig.label}
                  <Badge variant="outline">
                    {filteredInvestors.length} Total
                  </Badge>
                </CardTitle>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px] max-w-[300px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="search-investors-input"
                  />
                </div>

                <Select value={stateFilter} onValueChange={setStateFilter}>
                  <SelectTrigger
                    className="w-[180px]"
                    data-testid="state-filter"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by State" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All States</SelectItem>
                    {availableStates.map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {(searchQuery || stateFilter !== "all") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearchQuery("");
                      setStateFilter("all");
                    }}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="loading-spinner border-blue-600"></div>
              </div>
            ) : paginatedInvestors.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No {roleConfig.label.toLowerCase()} found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="investors-table">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">
                          {roleConfig.singular}
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">
                          Email
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">
                          State
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">
                          Reserved
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">
                          Closed
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">
                          Status
                        </th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedInvestors.map((investor) => (
                        <tr
                          key={investor.user_id}
                          className="border-b hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => handleInvestorClick(investor)}
                          data-testid={`investor-row-${investor.user_id}`}
                        >
                          <td className="py-3 px-4">
                            <p className="font-medium">{investor.name}</p>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-sm">{investor.email}</p>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-sm">{investor.state || "N/A"}</p>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge
                              variant="outline"
                              className="bg-blue-500/10 text-blue-500"
                            >
                              {investor.deals_reserved || 0}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge
                              variant="outline"
                              className="bg-green-500/10 text-green-500"
                            >
                              {investor.deals_closed || 0}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            {investor.is_active !== false ? (
                              <Badge className="bg-green-500/10 text-green-500">
                                Active
                              </Badge>
                            ) : (
                              <Badge className="bg-red-500/10 text-red-500">
                                Disabled
                              </Badge>
                            )}
                          </td>
                          <td
                            className="py-3 px-4"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center gap-2">
                              {!investor.invited_at && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSendInvite([investor.user_id]);
                                  }}
                                  title="Send Invite"
                                  data-testid={`invite-${investor.user_id}`}
                                >
                                  <Send className="w-4 h-4 text-blue-500" />
                                </Button>
                              )}
                              {investor.is_active !== false ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleInvestorAction(
                                      investor.user_id,
                                      "disable",
                                    );
                                  }}
                                  title={`Disable ${roleConfig.singular}`}
                                  data-testid={`disable-${investor.user_id}`}
                                >
                                  <UserX className="w-4 h-4 text-red-500" />
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleInvestorAction(
                                      investor.user_id,
                                      "enable",
                                    );
                                  }}
                                  title={`Enable ${roleConfig.singular}`}
                                  data-testid={`enable-${investor.user_id}`}
                                >
                                  <Play className="w-4 h-4 text-green-500" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{" "}
                      {Math.min(
                        currentPage * ITEMS_PER_PAGE,
                        filteredInvestors.length,
                      )}{" "}
                      of {filteredInvestors.length}{" "}
                      {roleConfig.label.toLowerCase()}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setCurrentPage((p) => Math.max(1, p - 1))
                        }
                        disabled={currentPage === 1}
                        data-testid="prev-page"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Previous
                      </Button>
                      <div className="flex items-center gap-1">
                        {Array.from(
                          { length: Math.min(5, totalPages) },
                          (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            return (
                              <Button
                                key={pageNum}
                                variant={
                                  currentPage === pageNum
                                    ? "default"
                                    : "outline"
                                }
                                size="sm"
                                className="w-8 h-8 p-0"
                                onClick={() => setCurrentPage(pageNum)}
                              >
                                {pageNum}
                              </Button>
                            );
                          },
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setCurrentPage((p) => Math.min(totalPages, p + 1))
                        }
                        disabled={currentPage === totalPages}
                        data-testid="next-page"
                      >
                        Next
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Investor Detail / Edit Modal */}
      <Dialog
        open={showDetailModal}
        onOpenChange={(open) => {
          setShowDetailModal(open);
          if (!open) setEditMode(false);
        }}
      >
        <DialogContent
          className={
            editMode
              ? "max-w-2xl max-h-[90vh] overflow-hidden p-0"
              : "max-w-lg max-h-[90vh] overflow-hidden p-0"
          }
        >
          <DialogHeader className={editMode ? "p-6 pb-4" : "p-6 pb-4"}>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              {editMode
                ? `Edit ${roleConfig.singular}`
                : `${roleConfig.singular} Details`}
            </DialogTitle>
            {editMode && (
              <DialogDescription>
                Update the investor's details below.
              </DialogDescription>
            )}
          </DialogHeader>

          {selectedInvestor && !editMode && (
            <ScrollArea className="max-h-[60vh] px-6">
              <div className="space-y-6 pb-6">
                {/* Basic Info */}
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-600 to-yellow-500 flex items-center justify-center text-white text-xl font-bold">
                    {selectedInvestor.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold">
                      {selectedInvestor.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      {selectedInvestor.is_active !== false ? (
                        <Badge className="bg-green-500/10 text-green-500">
                          Active
                        </Badge>
                      ) : (
                        <Badge className="bg-red-500/10 text-red-500">
                          Disabled
                        </Badge>
                      )}
                      {selectedInvestor.imported_at && (
                        <Badge variant="outline">Imported</Badge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedInvestor.email}</span>
                  </div>
                  {selectedInvestor.phone && (
                    <div className="flex items-center gap-3 text-sm">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span>{selectedInvestor.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-sm">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span>
                      {selectedInvestor.address ||
                        selectedInvestor.state ||
                        "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span>
                      Joined: {formatDate(selectedInvestor.created_at)}
                    </span>
                  </div>
                </div>

                {/* Investment Criteria */}
                {(selectedInvestor.available_capital ||
                  (selectedInvestor.primary_investment_focus &&
                    (Array.isArray(selectedInvestor.primary_investment_focus)
                      ? selectedInvestor.primary_investment_focus.length > 0
                      : true)) ||
                  selectedInvestor.years_experience) && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Investment Criteria
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      {selectedInvestor.available_capital && (
                        <div className="p-2 rounded-lg bg-muted/50">
                          <p className="text-xs text-muted-foreground">
                            Liquid Capital
                          </p>
                          <p className="text-sm font-medium">
                            {selectedInvestor.available_capital}
                          </p>
                        </div>
                      )}
                      {selectedInvestor.primary_investment_focus &&
                        (Array.isArray(
                          selectedInvestor.primary_investment_focus,
                        )
                          ? selectedInvestor.primary_investment_focus.length > 0
                          : true) && (
                          <div className="p-2 rounded-lg bg-muted/50 col-span-2">
                            <p className="text-xs text-muted-foreground mb-1">
                              Primary Focus
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {(Array.isArray(
                                selectedInvestor.primary_investment_focus,
                              )
                                ? selectedInvestor.primary_investment_focus
                                : [selectedInvestor.primary_investment_focus]
                              ).map((focus) => (
                                <Badge
                                  key={focus}
                                  variant="secondary"
                                  className="text-xs"
                                >
                                  {focus}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      {selectedInvestor.years_experience != null && (
                        <div className="p-2 rounded-lg bg-muted/50">
                          <p className="text-xs text-muted-foreground">
                            Experience
                          </p>
                          <p className="text-sm font-medium">
                            {selectedInvestor.years_experience} years
                          </p>
                        </div>
                      )}
                      {selectedInvestor.areas_of_focus_state && (
                        <div className="p-2 rounded-lg bg-muted/50">
                          <p className="text-xs text-muted-foreground">
                            Focus Area
                          </p>
                          <p className="text-sm font-medium">
                            {selectedInvestor.areas_of_focus_state}
                          </p>
                          {selectedInvestor.areas_of_focus_regions?.length >
                            0 && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {selectedInvestor.areas_of_focus_regions.join(
                                ", ",
                              )}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Yes/No Criteria */}
                {(selectedInvestor.is_wholesaler ||
                  selectedInvestor.hard_money_funding ||
                  selectedInvestor.comfortable_wholesale_fee ||
                  selectedInvestor.agrees_finder_fee ||
                  selectedInvestor.will_provide_relisting) && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Investor Profile
                    </h4>
                    <div className="space-y-2">
                      {[
                        { key: "is_wholesaler", label: "Wholesaler" },
                        {
                          key: "hard_money_funding",
                          label: "Hard Money Funding",
                        },
                        {
                          key: "comfortable_wholesale_fee",
                          label: "Comfortable w/ Wholesale Fee",
                        },
                        {
                          key: "agrees_finder_fee",
                          label: "Agrees to 1% Finder Fee",
                        },
                        {
                          key: "will_provide_relisting",
                          label: "Will Relist After Renovation",
                        },
                      ]
                        .filter(({ key }) => selectedInvestor[key])
                        .map(({ key, label }) => (
                          <div
                            key={key}
                            className="flex items-center justify-between text-sm py-1 px-2 rounded bg-muted/30"
                          >
                            <span className="text-muted-foreground">
                              {label}
                            </span>
                            <Badge
                              variant={
                                selectedInvestor[key] === "yes"
                                  ? "default"
                                  : "outline"
                              }
                              className={
                                selectedInvestor[key] === "yes"
                                  ? "bg-green-500/10 text-green-600"
                                  : "bg-red-500/10 text-red-500"
                              }
                            >
                              {selectedInvestor[key] === "yes" ? "Yes" : "No"}
                            </Badge>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 rounded-lg bg-blue-500/10">
                    <p className="text-2xl font-bold text-blue-600">
                      {selectedInvestor.deals_reserved || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">Reserved</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-purple-500/10">
                    <p className="text-2xl font-bold text-purple-600">
                      {selectedInvestor.deals_in_closing || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">In Closing</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-green-500/10">
                    <p className="text-2xl font-bold text-green-600">
                      {selectedInvestor.deals_closed || 0}
                    </p>
                    <p className="text-xs text-muted-foreground">Closed</p>
                  </div>
                </div>

                {/* NDAs */}
                <div className="flex items-center gap-3 text-sm p-3 rounded-lg bg-muted/50">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <span>
                    NDAs Signed: {selectedInvestor.nda_signed_count || 0}
                  </span>
                </div>

                {/* Notes */}
                {selectedInvestor.notes && (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground mb-1">Notes</p>
                    <p className="text-sm">{selectedInvestor.notes}</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}

          {/* Edit Mode Form */}
          {selectedInvestor && editMode && editInvestor && (
            <ScrollArea className="max-h-[60vh] px-6">
              <div className="space-y-6 pb-6">
                {/* Basic Information */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input
                        value={editInvestor.name}
                        onChange={(e) =>
                          setEditInvestor({
                            ...editInvestor,
                            name: e.target.value,
                          })
                        }
                        data-testid="edit-investor-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone Number</Label>
                      <Input
                        value={editInvestor.phone}
                        onChange={(e) =>
                          setEditInvestor({
                            ...editInvestor,
                            phone: e.target.value,
                          })
                        }
                        data-testid="edit-investor-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={editInvestor.email}
                        onChange={(e) =>
                          setEditInvestor({
                            ...editInvestor,
                            email: e.target.value,
                          })
                        }
                        data-testid="edit-investor-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <AddressAutocomplete
                        label="Address"
                        value={editInvestor.address}
                        onChange={(e) =>
                          setEditInvestor({
                            ...editInvestor,
                            address: e.target.value,
                          })
                        }
                        onPlaceSelected={(addressData) => {
                          setEditInvestor((prev) => ({
                            ...prev,
                            address:
                              addressData.formatted_address || prev.address,
                          }));
                        }}
                        placeholder="Enter address"
                        data-testid="edit-investor-address"
                      />
                    </div>
                  </div>
                </div>

                {/* Investment Details */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">
                    Investment Details
                  </h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Available Liquid Capital</Label>
                        <Input
                          value={editInvestor.available_capital}
                          onChange={(e) =>
                            setEditInvestor({
                              ...editInvestor,
                              available_capital: e.target.value,
                            })
                          }
                          data-testid="edit-investor-capital"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Primary Investment Focus</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-between font-normal h-auto min-h-[40px]"
                              data-testid="edit-investor-focus"
                            >
                              {editInvestor.primary_investment_focus.length >
                              0 ? (
                                <span className="flex flex-wrap gap-1">
                                  {editInvestor.primary_investment_focus.map(
                                    (item) => (
                                      <Badge
                                        key={item}
                                        variant="secondary"
                                        className="text-xs px-2 py-0.5 gap-1"
                                      >
                                        {item}
                                        <X
                                          className="w-3 h-3 cursor-pointer hover:text-destructive"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setEditInvestor((prev) => ({
                                              ...prev,
                                              primary_investment_focus:
                                                prev.primary_investment_focus.filter(
                                                  (f) => f !== item,
                                                ),
                                            }));
                                          }}
                                        />
                                      </Badge>
                                    ),
                                  )}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">
                                  Select investment focus
                                </span>
                              )}
                              <ChevronRight className="w-4 h-4 shrink-0 opacity-50 rotate-90" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-[280px] p-2"
                            align="start"
                          >
                            <div className="space-y-1 max-h-[240px] overflow-y-auto">
                              {INVESTMENT_FOCUS_OPTIONS.map((opt) => {
                                const isSelected =
                                  editInvestor.primary_investment_focus.includes(
                                    opt,
                                  );
                                return (
                                  <div
                                    key={opt}
                                    className="flex items-center space-x-2 rounded-md px-2 py-1.5 hover:bg-muted cursor-pointer"
                                    onClick={() => {
                                      setEditInvestor((prev) => ({
                                        ...prev,
                                        primary_investment_focus: isSelected
                                          ? prev.primary_investment_focus.filter(
                                              (f) => f !== opt,
                                            )
                                          : [
                                              ...prev.primary_investment_focus,
                                              opt,
                                            ],
                                      }));
                                    }}
                                  >
                                    <Checkbox
                                      checked={isSelected}
                                      onCheckedChange={() => {}}
                                    />
                                    <span className="text-sm">{opt}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Areas of Focus</Label>
                      <Select
                        value={editInvestor.areas_of_focus_state}
                        onValueChange={(value) =>
                          setEditInvestor({
                            ...editInvestor,
                            areas_of_focus_state: value,
                            areas_of_focus_regions: [],
                          })
                        }
                      >
                        <SelectTrigger data-testid="edit-investor-area-state">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map((state) => (
                            <SelectItem key={state} value={state}>
                              {state}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {editInvestor.areas_of_focus_state && (
                        <div className="flex flex-col gap-2 ml-2 mt-2 p-3 bg-muted/50 rounded-lg">
                          {["Northern", "Central", "Southern"].map((region) => {
                            const regionValue = `${region} ${editInvestor.areas_of_focus_state}`;
                            const isChecked =
                              editInvestor.areas_of_focus_regions.includes(
                                regionValue,
                              );
                            return (
                              <div
                                key={region}
                                className="flex items-center space-x-2"
                              >
                                <Checkbox
                                  checked={isChecked}
                                  onCheckedChange={(checked) => {
                                    setEditInvestor((prev) => ({
                                      ...prev,
                                      areas_of_focus_regions: checked
                                        ? [
                                            ...prev.areas_of_focus_regions,
                                            regionValue,
                                          ]
                                        : prev.areas_of_focus_regions.filter(
                                            (r) => r !== regionValue,
                                          ),
                                    }));
                                  }}
                                />
                                <Label className="cursor-pointer text-sm">
                                  {regionValue}
                                </Label>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Years of Investment Experience</Label>
                      <Input
                        type="number"
                        value={editInvestor.years_experience}
                        onChange={(e) =>
                          setEditInvestor({
                            ...editInvestor,
                            years_experience: e.target.value,
                          })
                        }
                        data-testid="edit-investor-experience"
                      />
                    </div>

                    {[
                      {
                        key: "is_wholesaler",
                        label: "Is the investor a wholesaler?",
                      },
                      {
                        key: "hard_money_funding",
                        label: "Is the investor funding with hard money?",
                      },
                      {
                        key: "comfortable_wholesale_fee",
                        label:
                          "Is the investor comfortable paying a wholesale fee if there's enough margin?",
                      },
                      {
                        key: "agrees_finder_fee",
                        label: "Agrees to 1% Finder Fee Buyer Commission?",
                      },
                      {
                        key: "will_provide_relisting",
                        label:
                          "Will provide property for relisting after renovation?",
                      },
                    ].map(({ key, label }) => (
                      <div key={key} className="space-y-2">
                        <Label>{label}</Label>
                        <RadioGroup
                          value={editInvestor[key]}
                          onValueChange={(value) =>
                            setEditInvestor({ ...editInvestor, [key]: value })
                          }
                          className="flex items-center gap-6"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="yes"
                              id={`edit-${key}-yes`}
                              data-testid={`edit-investor-${key}-yes`}
                            />
                            <Label
                              htmlFor={`edit-${key}-yes`}
                              className="cursor-pointer"
                            >
                              Yes
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="no"
                              id={`edit-${key}-no`}
                              data-testid={`edit-investor-${key}-no`}
                            />
                            <Label
                              htmlFor={`edit-${key}-no`}
                              className="cursor-pointer"
                            >
                              No
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">
                    Additional Notes
                  </h3>
                  <Textarea
                    value={editInvestor.notes}
                    onChange={(e) =>
                      setEditInvestor({
                        ...editInvestor,
                        notes: e.target.value,
                      })
                    }
                    rows={4}
                    data-testid="edit-investor-notes"
                  />
                </div>
              </div>
            </ScrollArea>
          )}

          <DialogFooter className="p-6 pt-4 border-t">
            {!editMode ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowDetailModal(false)}
                >
                  Close
                </Button>
                <Button
                  onClick={startEditInvestor}
                  data-testid="edit-investor-btn"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setEditMode(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveInvestor}
                  disabled={saving}
                  data-testid="save-investor-btn"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Single Investor Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden p-0">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle>Add New Investor</DialogTitle>
            <DialogDescription>
              Fill in the investor's details below. Fields marked with * are
              required.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh] px-6">
            <div className="space-y-6 pb-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="inv-name">Name *</Label>
                    <Input
                      id="inv-name"
                      placeholder="John Smith"
                      value={newInvestor.name}
                      onChange={(e) =>
                        setNewInvestor({ ...newInvestor, name: e.target.value })
                      }
                      data-testid="add-investor-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inv-phone">Phone Number *</Label>
                    <Input
                      id="inv-phone"
                      placeholder="+1-555-123-4567"
                      value={newInvestor.phone}
                      onChange={(e) =>
                        setNewInvestor({
                          ...newInvestor,
                          phone: e.target.value,
                        })
                      }
                      data-testid="add-investor-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inv-email">Email *</Label>
                    <Input
                      id="inv-email"
                      type="email"
                      placeholder="john@example.com"
                      value={newInvestor.email}
                      onChange={(e) =>
                        setNewInvestor({
                          ...newInvestor,
                          email: e.target.value,
                        })
                      }
                      data-testid="add-investor-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <AddressAutocomplete
                      id="inv-address"
                      label="Address"
                      value={newInvestor.address}
                      onChange={(e) =>
                        setNewInvestor({
                          ...newInvestor,
                          address: e.target.value,
                        })
                      }
                      onPlaceSelected={(addressData) => {
                        setNewInvestor((prev) => ({
                          ...prev,
                          address:
                            addressData.formatted_address || prev.address,
                        }));
                      }}
                      placeholder="123 Main St, City, ST"
                      data-testid="add-investor-address"
                    />
                  </div>
                </div>
              </div>

              {/* Investment Details */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">
                  Investment Details
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="inv-capital">
                        Available Liquid Capital
                      </Label>
                      <Input
                        id="inv-capital"
                        placeholder="$500,000"
                        value={newInvestor.available_capital}
                        onChange={(e) =>
                          setNewInvestor({
                            ...newInvestor,
                            available_capital: e.target.value,
                          })
                        }
                        data-testid="add-investor-capital"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Primary Investment Focus</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-between font-normal h-auto min-h-[40px]"
                            data-testid="add-investor-focus"
                          >
                            {newInvestor.primary_investment_focus.length > 0 ? (
                              <span className="flex flex-wrap gap-1">
                                {newInvestor.primary_investment_focus.map(
                                  (item) => (
                                    <Badge
                                      key={item}
                                      variant="secondary"
                                      className="text-xs px-2 py-0.5 gap-1"
                                    >
                                      {item}
                                      <X
                                        className="w-3 h-3 cursor-pointer hover:text-destructive"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setNewInvestor((prev) => ({
                                            ...prev,
                                            primary_investment_focus:
                                              prev.primary_investment_focus.filter(
                                                (f) => f !== item,
                                              ),
                                          }));
                                        }}
                                      />
                                    </Badge>
                                  ),
                                )}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">
                                Select investment focus
                              </span>
                            )}
                            <ChevronRight className="w-4 h-4 shrink-0 opacity-50 rotate-90" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[280px] p-2" align="start">
                          <div className="space-y-1 max-h-[240px] overflow-y-auto">
                            {INVESTMENT_FOCUS_OPTIONS.map((opt) => {
                              const isSelected =
                                newInvestor.primary_investment_focus.includes(
                                  opt,
                                );
                              return (
                                <div
                                  key={opt}
                                  className="flex items-center space-x-2 rounded-md px-2 py-1.5 hover:bg-muted cursor-pointer"
                                  onClick={() => {
                                    setNewInvestor((prev) => ({
                                      ...prev,
                                      primary_investment_focus: isSelected
                                        ? prev.primary_investment_focus.filter(
                                            (f) => f !== opt,
                                          )
                                        : [
                                            ...prev.primary_investment_focus,
                                            opt,
                                          ],
                                    }));
                                  }}
                                >
                                  <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={() => {}}
                                    data-testid={`focus-option-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                                  />
                                  <span className="text-sm">{opt}</span>
                                </div>
                              );
                            })}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Areas of Focus</Label>
                    <Select
                      value={newInvestor.areas_of_focus_state}
                      onValueChange={(value) =>
                        setNewInvestor({
                          ...newInvestor,
                          areas_of_focus_state: value,
                          areas_of_focus_regions: [],
                        })
                      }
                    >
                      <SelectTrigger data-testid="add-investor-area-state">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {newInvestor.areas_of_focus_state && (
                      <div className="flex flex-col gap-2 ml-2 mt-2 p-3 bg-muted/50 rounded-lg">
                        {["Northern", "Central", "Southern"].map((region) => {
                          const regionValue = `${region} ${newInvestor.areas_of_focus_state}`;
                          const isChecked =
                            newInvestor.areas_of_focus_regions.includes(
                              regionValue,
                            );
                          return (
                            <div
                              key={region}
                              className="flex items-center space-x-2"
                            >
                              <Checkbox
                                id={`region-${region}`}
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                  setNewInvestor((prev) => ({
                                    ...prev,
                                    areas_of_focus_regions: checked
                                      ? [
                                          ...prev.areas_of_focus_regions,
                                          regionValue,
                                        ]
                                      : prev.areas_of_focus_regions.filter(
                                          (r) => r !== regionValue,
                                        ),
                                  }));
                                }}
                                data-testid={`add-investor-region-${region.toLowerCase()}`}
                              />
                              <Label
                                htmlFor={`region-${region}`}
                                className="cursor-pointer text-sm"
                              >
                                {regionValue}
                              </Label>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="inv-experience">
                      Years of Investment Experience
                    </Label>
                    <Input
                      id="inv-experience"
                      type="number"
                      placeholder="5"
                      value={newInvestor.years_experience}
                      onChange={(e) =>
                        setNewInvestor({
                          ...newInvestor,
                          years_experience: e.target.value,
                        })
                      }
                      data-testid="add-investor-experience"
                    />
                  </div>

                  {/* Radio button questions */}
                  {[
                    {
                      key: "is_wholesaler",
                      label: "Is the investor a wholesaler?",
                    },
                    {
                      key: "hard_money_funding",
                      label: "Is the investor funding with hard money?",
                    },
                    {
                      key: "comfortable_wholesale_fee",
                      label:
                        "Is the investor comfortable paying a wholesale fee if there's enough margin?",
                    },
                    {
                      key: "agrees_finder_fee",
                      label: "Agrees to 1% Finder Fee Buyer Commission?",
                    },
                    {
                      key: "will_provide_relisting",
                      label:
                        "Will provide property for relisting after renovation?",
                    },
                  ].map(({ key, label }) => (
                    <div key={key} className="space-y-2">
                      <Label>{label}</Label>
                      <RadioGroup
                        value={newInvestor[key]}
                        onValueChange={(value) =>
                          setNewInvestor({ ...newInvestor, [key]: value })
                        }
                        className="flex items-center gap-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="yes"
                            id={`${key}-yes`}
                            data-testid={`add-investor-${key}-yes`}
                          />
                          <Label
                            htmlFor={`${key}-yes`}
                            className="cursor-pointer"
                          >
                            Yes
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="no"
                            id={`${key}-no`}
                            data-testid={`add-investor-${key}-no`}
                          />
                          <Label
                            htmlFor={`${key}-no`}
                            className="cursor-pointer"
                          >
                            No
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wide">
                  Additional Notes
                </h3>
                <Textarea
                  placeholder="Any additional notes about this investor..."
                  value={newInvestor.notes}
                  onChange={(e) =>
                    setNewInvestor({ ...newInvestor, notes: e.target.value })
                  }
                  rows={4}
                  data-testid="add-investor-notes"
                />
              </div>
            </div>
          </ScrollArea>

          <DialogFooter className="p-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddSingleInvestor}
              disabled={importing}
              data-testid="confirm-add-investor"
            >
              {importing ? "Adding..." : "Add Investor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={resetImportModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Import Investors
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to import multiple investors at once.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Instructions */}
            <div className="p-4 rounded-lg bg-muted/50">
              <h4 className="font-medium mb-2">CSV Format Requirements:</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>
                  • <strong>Required columns:</strong> name, email, state
                </li>
                <li>
                  • <strong>Optional columns:</strong> phone, notes
                </li>
                <li>• First row must be headers</li>
                <li>• Each investor will receive a temporary password</li>
              </ul>
              <Button
                variant="link"
                className="p-0 h-auto mt-2 text-blue-600"
                onClick={handleDownloadSample}
              >
                <Download className="w-4 h-4 mr-1" />
                Download sample template
              </Button>
            </div>

            {/* File Upload */}
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                ref={fileInputRef}
                className="hidden"
              />
              <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground mb-2">
                {importData.length > 0
                  ? `${importData.length} investors ready to import`
                  : "Drop a CSV file here or click to browse"}
              </p>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
              >
                Select CSV File
              </Button>
            </div>

            {/* Preview Data */}
            {importData.length > 0 && !importResults && (
              <div className="max-h-[200px] overflow-y-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0">
                    <tr>
                      <th className="text-left p-2">Name</th>
                      <th className="text-left p-2">Email</th>
                      <th className="text-left p-2">State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importData.slice(0, 10).map((row, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2">{row.name}</td>
                        <td className="p-2">{row.email}</td>
                        <td className="p-2">{row.state}</td>
                      </tr>
                    ))}
                    {importData.length > 10 && (
                      <tr className="border-t bg-muted/50">
                        <td
                          colSpan={3}
                          className="p-2 text-center text-muted-foreground"
                        >
                          ... and {importData.length - 10} more
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Import Results */}
            {importResults && (
              <div className="space-y-3">
                {importResults.success.length > 0 && (
                  <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        <span className="font-medium">
                          {importResults.success.length} Successfully Imported
                        </span>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          const ids = importResults.success.map(
                            (i) => i.user_id,
                          );
                          handleSendInvite(ids);
                        }}
                      >
                        <Send className="w-4 h-4 mr-1" />
                        Send Invites
                      </Button>
                    </div>
                    <div className="text-sm text-muted-foreground max-h-[100px] overflow-y-auto">
                      {importResults.success.map((item, i) => (
                        <div key={i} className="flex justify-between py-1">
                          <span>{item.email}</span>
                          <code className="text-xs bg-muted px-1 rounded">
                            {item.temp_password}
                          </code>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {importResults.skipped.length > 0 && (
                  <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                    <div className="flex items-center gap-2 text-yellow-600 mb-2">
                      <AlertCircle className="w-4 h-4" />
                      <span className="font-medium">
                        {importResults.skipped.length} Skipped
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {importResults.skipped.map((item, i) => (
                        <div key={i}>
                          {item.email}: {item.reason}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {importResults.failed.length > 0 && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <div className="flex items-center gap-2 text-red-600 mb-2">
                      <X className="w-4 h-4" />
                      <span className="font-medium">
                        {importResults.failed.length} Failed
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {importResults.failed.map((item, i) => (
                        <div key={i}>
                          {item.email}: {item.reason}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={resetImportModal}>
              {importResults ? "Close" : "Cancel"}
            </Button>
            {!importResults && (
              <Button
                onClick={handleImport}
                disabled={importData.length === 0 || importing}
                data-testid="confirm-import"
              >
                {importing
                  ? "Importing..."
                  : `Import ${importData.length} Investors`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default InvestorManagementPage;
